import { computed, MaybeRefOrGetter, toValue } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { graphql, useFragment } from '@/gql'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

const HireRecruiterFragment = graphql(`
  fragment HireRecruiterFields on Hire {
    id
    recruiter {
      id
      name
    }
    recruiterManagesWorkers @include(if: $hasRecruiterCapabilities)
    viewerIsSupplier
    supplierHire {
      id
    }
  }
`)

const GET_HIRE_RECRUITER = graphql(`
  query GetHireRecruiterStatus($id: ID!, $hasRecruiterCapabilities: Boolean!) {
    hire(id: $id) {
      ...HireRecruiterFields
    }
  }
`)

export function useHireRecruiter(hireId: MaybeRefOrGetter<string>) {
  const { isCompany, hasRecruiterCapabilities: hasRecruiterCaps } = useAccountCapabilities()

  const { result, loading, error, refetch } = useQuery(
    GET_HIRE_RECRUITER,
    () => ({
      id: toValue(hireId),
      hasRecruiterCapabilities: isCompany.value || hasRecruiterCaps.value,
    }),
    {
      clientId: 'publicApi',
    },
  )

  const hire = computed(() => useFragment(HireRecruiterFragment, result.value?.hire))

  const hasRecruiter = computed(() => !!hire.value?.recruiter)

  const viewerIsSupplier = computed(() => hire.value?.viewerIsSupplier ?? false)

  const recruiterManagesWorkers = computed(() => hire.value?.recruiterManagesWorkers ?? false)

  // True when this hire is the client-side mirror of a supplier hire — i.e.
  // the offer is going to a staffing agency, not directly to a worker, even
  // though the recruiter/supplier columns live on the supplier hire instead.
  const hasLinkedSupplierHire = computed(() => !!hire.value?.supplierHire)

  const hasRecruiterManagingWorkers = computed(
    () =>
      ((hasRecruiter.value || viewerIsSupplier.value) && recruiterManagesWorkers.value) ||
      hasLinkedSupplierHire.value,
  )

  const recruiterName = computed(() => hire.value?.recruiter?.name ?? '')

  return {
    hasRecruiter,
    viewerIsSupplier,
    recruiterManagesWorkers,
    hasRecruiterManagingWorkers,
    recruiterName,
    loading,
    error,
    refetch,
  }
}
